import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function SecurityPage(): Node {
  return html`
    <div>
      <h1>configureSecurity</h1>
      <p>Establish a security perimeter for remote module loading. Call <code>configureSecurity</code> once at app startup — before any <code>loadRemote</code> calls — to enforce origin whitelisting, SRI integrity checks, timeouts, and sandboxing.</p>

      ${Callout('If you have not used loadRemote yet, start with the <a href="/microfrontends/load-remote">loadRemote</a> page to understand how remote modules work, then come back here to lock them down.')}

      <h2>When Do You Need This?</h2>
      <p><code>loadRemote</code> works without <code>configureSecurity</code> — but in production you should always configure it to prevent untrusted code from executing in your app. It answers: <em>which origins can load code into my application?</em></p>

      <h2>Configuration</h2>
      ${CodeBlock(`import { configureSecurity } from 'onefold/remote';

configureSecurity({
  trustedOrigins: ['https://cdn.example.com', 'https://widgets.example.com'],
  requireIntegrity: true,
  blockAll: false,
  timeout: 10000,
});`)}

      <h2>Options</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>trustedOrigins</code></td><td>string[]</td><td>[]</td><td>Allowed origins for remote modules.</td></tr>
        <tr><td><code>requireIntegrity</code></td><td>boolean</td><td>false</td><td>Require SRI hash for all remote loads.</td></tr>
        <tr><td><code>blockAll</code></td><td>boolean</td><td>false</td><td>Block all remote loading (kill switch).</td></tr>
        <tr><td><code>timeout</code></td><td>number</td><td>10000</td><td>Maximum load time in ms before failing.</td></tr>
      </table>

      <h2>The 7 Security Layers</h2>
      <ol>
        <li><strong>Origin Allowlist</strong> — Only modules from <code>trustedOrigins</code> can be loaded. All other origins are rejected before any network request.</li>
        <li><strong>SRI Integrity</strong> — When <code>requireIntegrity</code> is true, every module must provide a hash. The fetched content is verified against the hash before execution.</li>
        <li><strong>Timeout</strong> — Modules that take longer than <code>timeout</code> ms are aborted. Prevents slow-loris attacks on the host application.</li>
        <li><strong>Isolation</strong> — Shadow DOM or iframe sandboxing prevents DOM access leaks between host and remote.</li>
        <li><strong>CSP Compatible</strong> — No <code>eval()</code>, no <code>Function()</code>, no inline scripts. Works with strict Content-Security-Policy headers.</li>
        <li><strong>Error Containment</strong> — Errors in remote modules are caught and contained. They cannot crash the host application.</li>
        <li><strong>Kill Switch</strong> — Set <code>blockAll: true</code> to instantly disable all remote module loading in production.</li>
      </ol>

      ${Callout('Always use requireIntegrity: true in production. Without it, a compromised CDN could serve malicious code that passes origin checks.', 'warn')}

      <h2>Production Example</h2>
      ${CodeBlock(`configureSecurity({
  trustedOrigins: [
    'https://cdn.yourcompany.com',
    'https://widgets.yourcompany.com',
  ],
  requireIntegrity: true,
  blockAll: false,
  timeout: 8000,
});`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/microfrontends/sri">SRI Integrity</a> — generating and verifying integrity hashes</li>
        <li><a href="/microfrontends/isolation">Isolation Modes</a> — Shadow DOM and iframe sandboxing</li>
        <li><a href="/microfrontends/deployment">Deployment</a> — deploying microfrontend architectures</li>
      </ul>
    </div>
  `;
}
